'use client';

import { useEffect, useState, useRef, useCallback } from 'react';
import { Slider } from '@mui/material';
import { LeftArrowOutlined, RightArrowOutlined } from '@zeelool/icon';
import {
  useInViewportAnimation,
  addAnimationClasses,
  handleAnimationError,
} from '@/hooks/useInViewportAnimation';
import '@/styles/animations.css';

interface ClearDrivingData {
  title?: string;
  description?: string;
  normal_img?: string;
  clear_img?: string;
  normal_word?: string;
  clear_word?: string;
}

interface ClearDrivingProps {
  drivingData: {
    clear_driving?: ClearDrivingData;
  };
  isMobile: boolean;
  isFromCache?: boolean;
  onExposure?: () => void;
}

export default function ClearDriving({
  drivingData,
  isMobile,
  isFromCache = false,
  onExposure,
}: ClearDrivingProps) {
  const [sliderPosition, setSliderPosition] = useState(isMobile ? 100 : 100);
  const [isSliderActive, setIsSliderActive] = useState(false);
  const [isInView, setIsInView] = useState(false);

  // Refs
  const compareBoxRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);
  const imageBeforeRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);

  // 如果从缓存恢复，确保元素可见
  useEffect(() => {
    if (isFromCache || isMobile) {
      setTimeout(() => {
        // 移除动画相关的opacity设置，确保元素可见
        const animationElements = ['clear-driving-box-word', 'clear-driving-box-compare'];

        animationElements.forEach((className) => {
          const elements = document.getElementsByClassName(className);
          Array.from(elements).forEach((el) => {
            const htmlEl = el as HTMLElement;
            htmlEl.style.opacity = '1';
            htmlEl.style.transform = 'scale(1) translateY(0)';
            // 移除可能存在的动画类
            htmlEl.classList.remove('fadeInDownAnimate', 'scaleToBigAnimate', 'opacity0');
          });
        });
      }, 100);
    }
  }, [isFromCache, isMobile]);

  // 动画触发器 - 基于Vue项目的实现，只在PC端生效
  const animationTriggerRef = useInViewportAnimation(
    () => {
      try {
        onExposure?.();

        // 如果从缓存恢复或移动端，跳过动画
        if (isFromCache || isMobile) return;

        // 清晰驾驶模块动画 - 对应Vue中的clear_driving动画
        const clearDrivingClasses = ['clear-driving-box-word'];
        const scaleDiv = 'clear-driving-box-compare';

        // 标题和描述文字从上淡入
        addAnimationClasses(clearDrivingClasses, 'fadeInDownAnimate');

        // 对比图片缩放淡入
        const compareElement = document.getElementsByClassName(scaleDiv)?.[0];
        if (compareElement) {
          compareElement.classList.add('scaleToBigAnimate');
        }
      } catch {
        handleAnimationError();
      }
    },
    { threshold: 0.1 },
  ); // 使用1.0阈值，确保完全进入视口才触发动画

  // 滚动相关状态（PC端）
  const scrollTopRef = useRef(0);
  const boxHeightRef = useRef(0);
  const topHeightRef = useRef(0);
  const lastTopHeightRef = useRef(0);

  const clearData = drivingData.clear_driving || {};

  // 移动端自动动画
  const startMobileAnimation = useCallback(() => {
    if (!isMobile || isInView) return;

    setIsInView(true);
    onExposure?.();

    const timer = setInterval(() => {
      setSliderPosition((prev) => {
        if (prev > 50) {
          return prev - 1;
        } else {
          clearInterval(timer);
          // 动画完成后移除过渡效果
          setTimeout(() => {
            if (imageBeforeRef.current) {
              imageBeforeRef.current.style.transition = 'none';
            }
          }, 200);
          return 50;
        }
      });
    }, 10);
  }, [isMobile, isInView, onExposure]);

  // PC端滚动处理 - 完全按照Vue逻辑
  const handleScroll = useCallback(() => {
    if (isMobile) return;

    try {
      const { scrollY } = window;
      const deltaY = scrollY > scrollTopRef.current ? 1 : -1;

      if (scrollY > topHeightRef.current && scrollY < lastTopHeightRef.current) {
        setIsSliderActive(true);

        // 完全按照Vue的计算方式
        const percent = Math.ceil(
          Math.abs(((scrollY - topHeightRef.current) / boxHeightRef.current) * 100),
        );
        const percent1 = Math.min(100, Math.max(0, percent));
        const num = Number(percent1);

        let newPosition = sliderPosition;

        if (deltaY > 0) {
          // 向下滚动，从右向左
          if (sliderPosition > 0) {
            const position1 = Math.max(0, Math.ceil(100 - num));
            newPosition = position1 < 10 ? 0 : position1;
          } else {
            setIsSliderActive(false);
            newPosition = 0;
          }
        } else if (sliderPosition < 100) {
          // 向上滚动，从左向右
          const position2 = Math.min(100, Math.ceil(100 - num));
          newPosition = position2 > 90 ? 100 : position2;
        } else {
          setIsSliderActive(false);
          newPosition = 100;
        }

        newPosition = Math.min(100, Math.max(0, newPosition));
        setSliderPosition(newPosition);

        // 直接操作DOM，与Vue一致
        if (sliderRef.current && imageBeforeRef.current) {
          if (newPosition === 100) {
            sliderRef.current.style.left = 'calc(100% - 6px)';
          } else {
            sliderRef.current.style.left = `${newPosition}%`;
          }
          imageBeforeRef.current.style.width = `${newPosition}%`;
        }
      } else if (isSliderActive) {
        setIsSliderActive(false);
      }

      scrollTopRef.current = scrollY;
    } catch {
      // do nothing
    }
  }, [isMobile, sliderPosition, isSliderActive]);

  // 移动端滑块变化处理
  const handleSliderChange = (value: number) => {
    setSliderPosition(value);
  };

  // 组件挂载时的初始化
  useEffect(() => {
    if (isMobile) {
      // 移动端：设置 Intersection Observer
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              startMobileAnimation();
            }
          });
        },
        { threshold: 0.5 },
      );

      if (compareBoxRef.current) {
        observer.observe(compareBoxRef.current);
      }

      return () => observer.disconnect();
    } else {
      // PC端：计算高度和位置 - 完全按照Vue逻辑
      const calculateDimensions = () => {
        const container = animationTriggerRef.current;
        if (container && titleRef.current && descRef.current && compareBoxRef.current) {
          const title = titleRef.current;
          const desc = descRef.current;
          const compare = compareBoxRef.current;

          // 按照Vue的计算方式
          topHeightRef.current = desc.offsetTop; // Vue: descEl.offsetTop

          // 寻找下一个模块（相当于Vue中的hotEl）
          const nextElement = container.nextElementSibling as HTMLElement;
          if (nextElement) {
            lastTopHeightRef.current = nextElement.offsetTop - compare.clientHeight;
          } else {
            // 如果没有下一个元素，使用容器底部
            lastTopHeightRef.current = container.offsetTop + container.offsetHeight;
          }
          boxHeightRef.current =
            container.clientHeight -
            title.clientHeight -
            desc.clientHeight -
            compare.clientHeight -
            100;
        }
      };

      // 延迟计算，确保DOM已渲染
      setTimeout(calculateDimensions, 100);

      // 添加滚动监听
      window.addEventListener('scroll', handleScroll, true);

      return () => {
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
  }, [isMobile, handleScroll, startMobileAnimation]);

  // 更新滑块位置的样式
  useEffect(() => {
    if (sliderRef.current && imageBeforeRef.current) {
      const position = sliderPosition === 100 ? 'calc(100% - 6px)' : `${sliderPosition}%`;
      sliderRef.current.style.left = position;
      imageBeforeRef.current.style.width = `${sliderPosition}%`;
    }
  }, [sliderPosition]);

  if (!clearData.normal_img || !clearData.clear_img) {
    return null;
  }

  return (
    <div
      ref={animationTriggerRef as React.RefObject<HTMLDivElement>}
      className={`clear-driving-box ${!isMobile ? 'clear-driving-box-word-title' : ''} ${isMobile ? 'mobile' : 'pc'}`}
      style={{
        width: '100%',
        height: isMobile ? 'auto' : '200vh', // PC端设置200vh创建滚动空间
        padding: isMobile ? '52px 16px 38px' : '0 0 4.6vw',
        margin: isMobile ? '0' : '5.2vw 0 0',
        background: isMobile
          ? 'linear-gradient(180deg, rgba(255,160,94,0) 0%, rgba(255,160,94,0.4) 54%, rgba(255,160,94,0) 100%)'
          : 'transparent',
        boxSizing: 'border-box',
      }}
    >
      <h2
        ref={titleRef}
        className={`title ${!isMobile ? 'clear-driving-box-word opacity0' : ''} ${isMobile ? 'mobile-title' : 'pc-title'} font-bs`}
        style={{
          textAlign: isMobile ? 'left' : 'center',
          fontSize: isMobile ? '20px' : '2.5vw',
          color: '#ffffff',
          marginBottom: isMobile ? '12px' : '0',
          paddingBottom: isMobile ? '0' : '1.2vw',
        }}
      >
        {clearData.title || 'Clear Driving Vision'}
      </h2>

      <p
        ref={descRef}
        className={`desc ${!isMobile ? 'clear-driving-box-word opacity0' : ''} ${isMobile ? 'mobile-desc' : 'pc-desc'}`}
        style={{
          fontSize: isMobile ? '11px' : '0.833vw',
          color: '#FFFFFF',
          lineHeight: isMobile ? '18px' : '1.25vw',
          textAlign: isMobile ? 'left' : 'center',
          marginBottom: isMobile ? '0' : '0',
          maxWidth: isMobile ? 'none' : '800px',
          margin: isMobile ? '0 0 24px 0' : '0 auto 40px auto',
        }}
      >
        {clearData.description ||
          'Experience enhanced visibility and reduced eye strain with our premium driving glasses collection.'}
      </p>

      <div
        ref={compareBoxRef}
        className={`compare-box ${!isMobile ? 'clear-driving-box-compare opacity0' : ''}`}
        style={{
          paddingTop: isMobile ? '24px' : '6vh',
          position: isMobile ? 'relative' : 'sticky', // PC端使用sticky定位
          top: isMobile ? 'auto' : 'calc(6vh + 2.083vw)', // 40px转vw (40/1920*100)
          width: isMobile ? 'auto' : '87.5vw', // 1680px转vw (1680/1920*100)
          margin: '0 auto',
        }}
      >
        <div
          className={`compare-container relative ${isMobile ? 'rounded-[20px] overflow-hidden' : ''}`}
          style={{
            width: '100%', // PC端和移动端都使用100%
            height: isMobile ? 'auto' : '76.2vh', // 移动端使用宽高比1.18:1，PC端使用vh单位
            borderRadius: isMobile ? '20px' : '2.083vw', // 40px转vw (40/1920*100)
            overflow: 'hidden',
            boxShadow: isMobile ? 'none' : '0 0 5.208vw 0 rgba(239, 157, 84, 0.5)', // 100px转vw (100/1920*100)
            aspectRatio: isMobile ? '343/443' : 'auto',
          }}
        >
          {isMobile ? (
            // 移动端布局
            <div className="image-wrapper absolute top-0 left-0 w-full h-full">
              {/* 前景图片容器 (普通图片) - 按滑块位置裁剪 */}
              <div
                ref={imageBeforeRef}
                className="image-before"
                style={{
                  height: '100%',
                  width: `${sliderPosition}%`,
                  overflow: 'hidden',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transition: 'none', // 移除过渡效果，让滑块和图片同步
                }}
              >
                <img
                  src={clearData.normal_img}
                  alt="Normal vision"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '200%', // 固定宽度，确保图片足够宽
                    objectFit: 'cover',
                    objectPosition: 'left center', // 从左侧开始显示
                  }}
                />
              </div>

              {/* 后景图片 (清晰图片) */}
              <img
                src={clearData.clear_img}
                alt="Clear vision"
                className="image image-after w-full h-full object-cover"
              />

              {/* 移动端MUI滑块控制器 - 简洁版本匹配Vue样式 */}
              <div className="mobile-slider-container absolute top-0 left-0 w-full h-full z-20">
                <Slider
                  value={sliderPosition}
                  onChange={(_, newValue) => handleSliderChange(newValue as number)}
                  min={0}
                  max={100}
                  step={1}
                  orientation="horizontal"
                  disableSwap
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    // 禁用点击轨道跳转
                    '& .MuiSlider-track': {
                      display: 'none',
                    },
                    '& .MuiSlider-rail': {
                      display: 'none',
                      pointerEvents: 'none', // 禁用轨道点击
                    },
                    '& .MuiSlider-root': {
                      cursor: 'default', // 移除点击光标
                    },
                    '& .MuiSlider-thumb': {
                      width: '40px',
                      height: '40px',
                      backgroundColor: 'transparent', // 设为透明
                      border: 'none',
                      borderRadius: '50%',
                      boxShadow: 'none', // 移除阴影
                      cursor: 'grab',
                      '&:active': {
                        cursor: 'grabbing',
                      },
                      '&:before': {
                        display: 'none',
                      },
                      '&:focus, &:hover, &.Mui-active, &.Mui-focusVisible': {
                        boxShadow: 'none', // 移除hover阴影
                      },
                      // 禁用键盘导航
                      '&[data-focusvisible="true"]': {
                        boxShadow: 'none',
                      },
                    },
                  }}
                />

                {/* 自定义滑块按钮内容 */}
                <div
                  className="absolute top-0 z-30 pointer-events-none flex items-center justify-center"
                  style={{
                    left: `${sliderPosition}%`,
                    transform: 'translateX(-50%)',
                    width: '40px',
                    height: '100%',
                  }}
                >
                  <div className="w-[40px] h-[40px] rounded-full bg-white flex items-center justify-center opacity-80">
                    <LeftArrowOutlined className="text-[12px] text-black mr-[3px]" />
                    <RightArrowOutlined className="text-[12px] text-black ml-[3px]" />
                  </div>
                </div>

                {/* 垂直分割线 */}
                <div
                  className="absolute top-0 z-10 pointer-events-none"
                  style={{
                    left: `${sliderPosition}%`,
                    transform: 'translateX(-50%)',
                    width: '2px',
                    height: '100%',
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 0 4px rgba(0,0,0,0.3)',
                  }}
                />
              </div>
            </div>
          ) : (
            // PC端布局 - 完全按照Vue版本结构
            <div
              className="image-wrapper"
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '87.5vw', // 1680px转vw
                height: '76.2vh',
                borderRadius: '2.083vw', // 40px转vw (40/1920*100)
                overflow: 'hidden',
              }}
            >
              {/* 前景图片 (普通图片) - 按滑块位置裁剪 */}
              <div
                ref={imageBeforeRef}
                className="image-before"
                style={{
                  height: '100%',
                  width: `${sliderPosition}%`,
                  overflow: 'hidden',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  transition: 'none',
                }}
              >
                <img
                  src={clearData.normal_img}
                  alt="Normal vision"
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: '87.5vw', // 固定宽度，与容器宽度一致
                    objectFit: 'cover',
                    objectPosition: 'left center', // 确保从左侧开始显示
                  }}
                />
              </div>

              {/* 后景图片 (清晰图片) */}
              <img
                src={clearData.clear_img}
                alt="Clear vision"
                className="image image-after"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'left center', // 与前景图片保持一致的对齐
                }}
              />

              {/* PC端滑块控制器 */}
              <div
                ref={sliderRef}
                className="slider absolute top-0 h-full bg-white cursor-ew-resize"
                style={{
                  left: sliderPosition === 100 ? 'calc(100% - 6px)' : `${sliderPosition}%`,
                  width: '6px',
                }}
              >
                <div className="line absolute top-0 left-0  w-full h-full bg-white" />

                {/* 标签 - 按照Vue样式 */}
                <div
                  className="words absolute flex items-center justify-center font-bs"
                  style={{
                    bottom: '1.25vw', // 24px转vw (24/1920*100)
                    left: '-14.167vw', // -272px转vw (-272/1920*100)
                    width: '28.646vw', // 550px转vw (550/1920*100)
                  }}
                >
                  <p
                    className="text-white text-right"
                    style={{
                      fontSize: '0.729vw', // 14px转vw (14/1920*100)
                      padding: '0 1.302vw', // 0 25px转vw (25/1920*100)
                      width: '14.323vw', // 275px转vw (275/1920*100)
                    }}
                  >
                    {clearData.normal_word || 'Normal'}
                  </p>
                  <p
                    className="text-white text-left"
                    style={{
                      fontSize: '0.729vw', // 14px转vw
                      padding: '0 1.302vw', // 0 25px转vw
                      width: '14.323vw', // 275px转vw
                    }}
                  >
                    {clearData.clear_word || 'Clear'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 移动端底部标签 */}
        {isMobile && (
          <div className="word-box flex justify-between mt-[10px]">
            <span className="text-white text-[10px]">{clearData.normal_word || 'Normal'}</span>
            <span className="text-white text-[10px]">{clearData.clear_word || 'Clear'}</span>
          </div>
        )}
      </div>
    </div>
  );
}
